import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteNav from '@/components/layout/SiteNav';
import SiteFooter from '@/components/layout/SiteFooter';
import HeroRing from '@/components/vani/HeroRing';
import ByOncoPlatformShowcase from '@/components/ByOncoPlatformShowcase';
import MetaTags from '@/components/SEO/MetaTags';
import useCatalogStats, { dash } from '@/hooks/useCatalogStats';
import { LANGUAGE_IO, TEXT_LANGUAGE_CHIPS, countTextLanguages } from '@/voice/languages';
import { SITE_ORIGIN, TALK_ORIGIN } from '@/config/site';
import { appUrl } from '@/config/hosts';
import { VANI_API } from '@/config/env';
import { useLanguage } from '@/i18n/LanguageProvider';
import { warmupVaniIsland } from '@/vani-island/mount';
import HowItWorksBento from '@/components/landing/HowItWorksBento';
import ClinicalScopeBento from '@/components/landing/ClinicalScopeBento';
import '@/styles/vani-landing.css';

const HOME_TITLE = 'ByOnco · The complete navigation platform for cancer care';
const HOME_DESCRIPTION = 'ByOnco is the complete navigation platform for cancer care. We get cancer patients to specialists, the fastest. Understand your diagnosis, match top cancer centers, calculate true costs, and access 24/7 care guidance.';

function fill(template, n) {
  return String(template || '').replaceAll('{n}', String(n));
}

function homeFaqs(t, n) {
  return [
    { q: t.faq1q, a: t.faq1a },
    { q: t.faq2q, a: t.faq2a },
    { q: t.faq3q, a: t.faq3a },
    { q: t.faq4q, a: fill(t.faq4a, n) },
    { q: t.faq5q, a: t.faq5a },
  ];
}

const LANGS = TEXT_LANGUAGE_CHIPS;

function Check() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function Reveal({ children, className = '', ...rest }) {
  return (
    <div className={`vani-reveal ${className}`.trim()} {...rest}>
      {children}
    </div>
  );
}

export default function VaniLanding() {
  const { t, setLang, lang: currentLang } = useLanguage();
  const stats = useCatalogStats();
  const hospitals = dash(stats?.hospitals_mapped);
  const cancers = dash(stats?.cancer_types_supported);
  const [sttOn, setSttOn] = useState(false);
  const langCount = countTextLanguages(LANGUAGE_IO);
  const faqs = homeFaqs(t, langCount);
  const liveCopy = fill(sttOn ? t.speakInLangs : t.askWriting, langCount);
  const liveHeadline = fill(sttOn ? t.speakInLangs : t.askWriting, langCount);

  useEffect(() => {
    warmupVaniIsland();
    const origin = String(VANI_API || '').replace(/\/$/, '');
    if (!origin) return undefined;
    fetch(`${origin}/api/agent/capabilities`, { credentials: 'include' })
      .then((r) => r.json())
      .then((b) => {
        if (b && typeof b.sttConfigured === 'boolean') setSttOn(!!b.sttConfigured);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const nodes = document.querySelectorAll('.vani-home .vani-reveal');
    if (!nodes.length || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('in');
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    nodes.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const homeJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'ByOnco',
      alternateName: 'ByOnco Cancer Care Navigation',
      url: `${SITE_ORIGIN}/`,
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Web',
      description: HOME_DESCRIPTION,
      offers: { '@type': 'Offer', price: '19.99', priceCurrency: 'USD' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    },
  ];

  return (
    <div className="vani-home">
      <MetaTags title={HOME_TITLE} description={HOME_DESCRIPTION} path="/" jsonLd={homeJsonLd} />
      <SiteNav homeHash />

      <header className="vani-hero" id="top">
        <div className="vani-wrap">
          <h1>
            {t.heroLine1}
            <br />
            <span className="vani-grad">{t.heroLine2}</span>
          </h1>
          <p className="vani-lede">{t.heroLede}</p>
          <div className="vani-cta-row">
            <a className="vani-btn vani-btn-primary" href={appUrl('/')}>
              {t.openTheApp} <span className="disc">→</span>
            </a>
            <a className="vani-btn vani-btn-ghost" href="#how">{t.seeHow}</a>
          </div>
          <p className="vani-under">
            {liveCopy}
            <span className="vani-under-cred">{t.trainedBy}</span>
          </p>
          <HeroRing />
        </div>
      </header>

      <div className="vani-strip">
        <div className="vani-wrap">
          <div className="vani-stat">
            <b>{langCount}</b>
            <span>{sttOn ? t.langStatSpeak : t.langStatWrite}</span>
          </div>
          <div className="vani-stat">
            <b>{cancers}</b>
            <span>{t.cancerTypesCovered}</span>
          </div>
          <div className="vani-stat">
            <b>{hospitals}</b>
            <span>{t.hospitalsMapped}</span>
          </div>
          <div className="vani-stat">
            <b>&lt;12h</b>
            <span>{t.secondOpinions}</span>
          </div>
        </div>
      </div>

      <section id="problem">
        <div className="vani-wrap vani-split">
          <Reveal>
            <div className="vani-eyebrow">{t.problemEyebrow}</div>
            <h2>{t.problemH2}</h2>
            <p className="vani-sub">{t.problemP1}</p>
            <p className="vani-sub">{t.problemP2}</p>
          </Reveal>
          <Reveal>
            <div className="vani-quote">
              “{t.problemQuote}”
              <small>{t.problemAttr}</small>
            </div>
          </Reveal>
        </div>
      </section>

      <HowItWorksBento />

      <section id="languages">
        <div className="vani-wrap vani-center">
          <Reveal>
            <div className="vani-eyebrow">{t.langEyebrow}</div>
            <h2>{liveHeadline}</h2>
            <p className="vani-sub">{fill(t.langSub, langCount)}</p>
          </Reveal>
          <Reveal className="vani-langs">
            {LANGS.map((lang) => {
              const io = LANGUAGE_IO.find((row) => row.code === lang.code);
              return (
                <span
                  key={lang.code}
                  className={`deva${currentLang === lang.code ? ' is-on' : ''}`}
                  title={io?.tts ? t.voiceReply : t.textReply}
                  data-tts={io?.tts ? '1' : '0'}
                  role="button"
                  tabIndex={0}
                  onClick={() => setLang(lang.code)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setLang(lang.code);
                    }
                  }}
                >
                  {lang.label}
                </span>
              );
            })}
          </Reveal>
        </div>
      </section>

      <ClinicalScopeBento stats={stats} />

      <ByOncoPlatformShowcase stats={stats} />

      <section id="safety">
        <div className="vani-wrap">
          <Reveal>
            <div className="vani-eyebrow">{t.safetyEyebrow}</div>
            <h2>{t.safetyH2}</h2>
            <p className="vani-sub">{t.safetySub}</p>
          </Reveal>
          <Reveal className="vani-safety">
            <div><span className="k">{t.safetyRedK}</span><p>{t.safetyRedP}</p></div>
            <div><span className="k">{t.safetyInventK}</span><p>{t.safetyInventP}</p></div>
            <div><span className="k">{t.safetyBoundK}</span><p>{t.safetyBoundP}</p></div>
            <div><span className="k">{t.safetyDistressK}</span><p>{t.safetyDistressP}</p></div>
            <div><span className="k">{t.safetyPrivK}</span><p>{t.safetyPrivP}</p></div>
          </Reveal>
        </div>
      </section>

      <section id="pricing" className="vani-price-band">
        <div className="vani-wrap">
          <Reveal className="vani-center">
            <div className="vani-eyebrow">{t.priceEyebrow}</div>
            <h2>{t.priceH2}</h2>
            <p className="vani-sub">{t.priceSub}</p>
          </Reveal>
          <div className="vani-plans">
            <Reveal className="vani-plan feature">
              <span className="tag">{t.planProTag}</span>
              <h3>{t.planPro}</h3>
              <div className="who">{t.planProWho}</div>
              <div className="vani-price">
                <span className="vani-price-now">$19.99</span>
                <span className="vani-price-period">/mo</span>
                <s className="vani-price-was">$39.99</s>
              </div>
              <ul>
                <li><Check />{fill(t.planPro1, langCount)}</li>
                <li><Check />{t.planPro2}</li>
                <li><Check />{t.planPro3}</li>
                <li><Check />{t.planPro4}</li>
                <li><Check />{t.planPro5}</li>
              </ul>
              <Link className="vani-btn vani-btn-primary" to="/subscribe?plan=monthly">{t.startPro} <span className="disc">→</span></Link>
            </Reveal>
            <Reveal className="vani-plan">
              <span className="tag" style={{ background: '#059669' }}>{t.planYearlyTag}</span>
              <h3>{t.planYearly}</h3>
              <div className="who">{t.planYearlyWho}</div>
              <div className="vani-price">
                <span className="vani-price-now">$14.99</span>
                <span className="vani-price-period">/mo</span>
                <s className="vani-price-was">$19.99</s>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -6, marginBottom: 14 }}>
                {t.planYearly2}
              </div>
              <ul>
                <li><Check />{t.planYearly1}</li>
                <li><Check />{fill(t.planPro1, langCount)}</li>
                <li><Check />{t.planPro2}</li>
                <li><Check />{t.planPro3}</li>
                <li><Check />{t.planYearly3}</li>
              </ul>
              <Link className="vani-btn vani-btn-ghost" to="/subscribe?plan=yearly">{t.startYearly} <span className="disc">→</span></Link>
            </Reveal>
            <Reveal className="vani-plan">
              <h3>{t.planLife}</h3>
              <div className="who">{t.planLifeWho}</div>
              <div className="vani-price">$999</div>
              <ul>
                <li><Check />{t.planLife1}</li>
                <li><Check />{t.planLife2}</li>
                <li><Check />{t.planLife3}</li>
              </ul>
              <Link className="vani-btn vani-btn-ghost" to="/subscribe?plan=lifetime">{t.getLifetime}</Link>
            </Reveal>
          </div>
          <p className="vani-sub vani-center" style={{ marginTop: 26, fontSize: 14 }}>
            {t.saasLead} <b>{t.saasBold}</b> {t.saasRest}{' '}
            <Link to="/for-hospitals" style={{ color: 'var(--violet)' }}>{t.requestDemo} →</Link>
          </p>
        </div>
      </section>

      <section id="faq">
        <div className="vani-wrap" style={{ maxWidth: 860 }}>
          <Reveal className="vani-center" style={{ marginBottom: 36 }}>
            <div className="vani-eyebrow">{t.faqEyebrow}</div>
            <h2>{t.faqH2}</h2>
          </Reveal>
          <Reveal>
            {faqs.map((item, i) => (
              <details key={item.q} open={i === 0}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="vani-close" id="contact">
        <div className="vani-wrap">
          <Reveal>
            <h2 className="vani-center" style={{ maxWidth: '22ch' }}>{t.closeH2}</h2>
            <p className="vani-sub vani-center">{t.closeSub}</p>
            <div className="vani-cta-row">
              <a className="vani-btn vani-btn-primary" href={appUrl('/')}>{t.openTheApp} <span className="disc">→</span></a>
              <a className="vani-btn vani-btn-ghost" href="mailto:care@getbyonco.com">{t.talkTeam}</a>
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
